import { useMutation } from '@tanstack/react-query'
import React, { useRef, useState } from 'react'

import ActionButton from '@/components/misc/ActionButton'
import EmailAddresses from '@/components/misc/EmailAddresses/EmailAddresses'
import ConfirmSendToColleagueModal from '@/components/misc/InviteOrRequestReferralPartners/components/ConfirmSendToColleagueModal'
import InvitingColleagueWarningMessage from '@/components/misc/InviteOrRequestReferralPartners/components/InvitingColleagueWarningMessage'
import PreviewInviteButton from '@/components/misc/InviteOrRequestReferralPartners/components/PreviewInviteButton'
import SuccessfulInvites from '@/components/misc/InviteOrRequestReferralPartners/components/SuccessfulInvites'
import UnsuccessfulInvites from '@/components/misc/InviteOrRequestReferralPartners/components/UnsuccesfulInvites'
import { SubmissionResult } from '@/components/misc/InviteOrRequestReferralPartners/types'
import CopyLink from '@/components/misc/PanelInviteEmailTemplate/CopyLink'
import { Alert, AlertDescription } from '@/components/ui/alert'
import useCurrentCompanyOrFail from '@/hooks/useCurrentCompanyOrFail'
import { useCurrentUserOrFail } from '@/hooks/useCurrentUserOrFail'
import useDialog from '@/hooks/useDialog'
import { useInvalidateQueries } from '@/hooks/useInvalidateQueries'
import usePosthogCapture from '@/hooks/usePosthogCapture/usePosthogCapture'
import { useShowGenericServerError } from '@/hooks/useShowGenericServerError'
import { isValidEmail } from '@/lib/helpers/emailAddressHelpers'
import { areEmailDomainsTheSame } from '@/lib/helpers/helperFunctions'
import { QUERY_KEYS } from '@/lib/queryKeys'
import companyService from '@/lib/services/companyService'
import errorTypeService from '@/lib/services/errorTypeService'
import inviteReferralPartnerService from '@/lib/services/inviteReferralPartnerService'
import { cn } from '@/lib/utils'

interface Props {
  className?: string
  // Addresses to open the form with. Anything prefilled that isn't a valid
  // address is left in place for the user to fix, with sending disabled until
  // they do.
  initialEmails?: string[]
}

const InviteReferralPartners: React.FC<Props> = ({
  className,
  initialEmails = [],
}) => {
  const currentCompany = useCurrentCompanyOrFail()
  const currentUser = useCurrentUserOrFail()
  const showGenericServerError = useShowGenericServerError()
  const { posthogCapture } = usePosthogCapture()
  const confirmSendToColleagueDialog = useDialog()
  const [emailAddresses, setEmailAddresses] = useState<string[]>(initialEmails)
  const [areEmailsValid, setAreEmailsValid] = useState(
    initialEmails.length > 0 && initialEmails.every(isValidEmail),
  )
  const [submissionResults, setSubmissionResults] = useState<
    SubmissionResult[]
  >([])
  const invalidateQueries = useInvalidateQueries()
  const areEmailsInvalid = !areEmailsValid
  const domElementRef = useRef<HTMLDivElement>(null)

  const createInviteMutation = useMutation({
    mutationFn: async (email: string) =>
      inviteReferralPartnerService.createInvite({ email }),
    onSuccess: async (data) => {
      setSubmissionResults([
        ...submissionResults,
        { email: data.email, success: true },
      ])

      await invalidateQueries([
        QUERY_KEYS.panelInvites(),
        QUERY_KEYS.networkOnboarding,
      ])
    },
    onMutate: () => {
      posthogCapture({
        appArea: 'relationship-management',
        action: 'invite-sent-via-rq',
      })
    },
    onError: async (error, email) => {
      if (errorTypeService.isConstraintViolationListError(error)) {
        const violations = error.response?.data.violations ?? []
        const violationMessages = violations.map(
          (violation) => violation.message,
        )

        if (violationMessages.length > 0) {
          setSubmissionResults([
            ...submissionResults,
            {
              email,
              messages: violationMessages,
              success: false,
            },
          ])
        }

        return
      }

      showGenericServerError()
    },
  })

  const successfulSubmissionResults = submissionResults.filter(
    (submissionResult) => submissionResult.success,
  )
  const unsuccessfulSubmissionResults = submissionResults.filter(
    (submissionResult) => !submissionResult.success,
  )

  const shareLink = companyService.getInvitationLink(
    currentCompany.referralCode,
  )

  const recipientsWithSameEmailDomain = getRecipientsWithSameEmailDomain({
    userEmail: currentUser.email,
    recipientEmails: emailAddresses,
  })

  async function sendInvite() {
    if (
      recipientsWithSameEmailDomain.length > 0 &&
      !confirmSendToColleagueDialog.isShowingDialog
    ) {
      confirmSendToColleagueDialog.showDialog()
      return
    }

    setSubmissionResults([])

    if (areEmailsInvalid || emailAddresses.length === 0) {
      // Our validation should prevent this from ever happening but just in case
      return
    }

    for (const emailAddress of emailAddresses) {
      // Send each invite request sequentially as it's easier for the API to
      // process and the results UI works better this way.
      try {
        await createInviteMutation.mutateAsync(emailAddress)
      } catch {
        // Do nothing as the createInviteMutation's onError callback handles errors
      }
    }

    // Reset the form to help avoid duplicate submissions
    const failedEmailInvites = unsuccessfulSubmissionResults.map(
      (submissionResult) => submissionResult.email,
    )

    setEmailAddresses(failedEmailInvites)
  }

  const isProcessing = createInviteMutation.isPending

  const isBeingShownInsideAModal = !!domElementRef.current?.closest('.modal')

  // If we're showing this component inside a modal, ensure we hide the contents
  // of the background modal when we show the confirmation modal
  const shouldHideMainForm =
    isBeingShownInsideAModal && confirmSendToColleagueDialog.isShowingDialog

  return (
    <div ref={domElementRef}>
      <form className={cn(className, { hidden: shouldHideMainForm })}>
        {successfulSubmissionResults.length > 0 && (
          <SuccessfulInvites invites={successfulSubmissionResults} />
        )}

        {unsuccessfulSubmissionResults.length > 0 && (
          <UnsuccessfulInvites invites={unsuccessfulSubmissionResults} />
        )}

        {submissionResults.length > 0 && (
          <div className="mb-2 text-black">Send more invites</div>
        )}

        <div className="mb-4">
          <EmailAddresses
            emails={emailAddresses}
            onChange={(newEmails, hasErrors) => {
              setEmailAddresses(newEmails)
              setAreEmailsValid(newEmails.length > 0 && !hasErrors)
            }}
          />
        </div>

        {recipientsWithSameEmailDomain.length > 0 && (
          <Alert variant="warning" className="my-2">
            <AlertDescription className="text-xs leading-normal">
              <InvitingColleagueWarningMessage
                recipientsWithSameEmailDomain={recipientsWithSameEmailDomain}
              />
            </AlertDescription>
          </Alert>
        )}

        <div
          className="mb-2 flex flex-col-reverse justify-between gap-4 sm:mb-4
            sm:flex-row sm:items-start"
        >
          <CopyLink
            variant="link"
            textToCopy={shareLink}
            buttonText="Or copy your unique invite link"
            className="p-0"
          />

          <ActionButton
            isProcessing={isProcessing}
            isProcessingText="Sending"
            disabled={!areEmailsValid || isProcessing}
            variant="primary"
            size="lg"
            onClick={sendInvite}
          >
            Send invite
          </ActionButton>
        </div>

        <div className="flex justify-center sm:justify-end">
          <PreviewInviteButton />
        </div>
      </form>

      {confirmSendToColleagueDialog.isShowingDialog && (
        <ConfirmSendToColleagueModal
          recipientsWithSameEmailDomain={recipientsWithSameEmailDomain}
          isProcessing={isProcessing}
          onSend={async () => {
            await sendInvite()
            confirmSendToColleagueDialog.hideDialog()
          }}
          onHide={confirmSendToColleagueDialog.hideDialog}
        />
      )}
    </div>
  )
}

function getRecipientsWithSameEmailDomain({
  userEmail,
  recipientEmails,
}: {
  userEmail: string
  recipientEmails: string[]
}): string[] {
  return recipientEmails.filter((recipientEmail) =>
    areEmailDomainsTheSame(userEmail, recipientEmail),
  )
}

export default InviteReferralPartners
